import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LayoutTemplate, Save, Upload, Plus, Trash2, ExternalLink, UploadCloud, Mail, Send, Eye } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { ICON_OPTIONS, resolveLandingImg } from "@/lib/landingIcons";

const Field = ({ label, value, onChange, textarea, placeholder, testid }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">{label}</span>
    {textarea ? (
      <textarea
        data-testid={testid}
        className="input-soft mt-1.5 w-full min-h-[90px]"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    ) : (
      <input
        data-testid={testid}
        className="input-soft mt-1.5 w-full"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    )}
  </label>
);

const Card = ({ title, children }) => (
  <section className="rounded-2xl border border-[#1E3F33]/10 bg-white p-6 space-y-4">
    <h2 className="font-heading text-lg text-[#1E3F33]">{title}</h2>
    {children}
  </section>
);

export default function AdminMarketing() {
  const [c, setC] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // ---- Campaign email composer state ----
  const [em, setEm] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [activeTid, setActiveTid] = useState("");
  const [recipients, setRecipients] = useState("");
  const [emSaving, setEmSaving] = useState(false);
  const [emSending, setEmSending] = useState(false);
  const [emPreview, setEmPreview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [supportEmail, setSupportEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  const loadGeneral = async () => {
    try {
      const { data } = await api.get("/admin/settings/general");
      setSupportEmail(data.support_email || "");
    } catch (e) { /* non-fatal */ }
  };

  const saveSupportEmail = async () => {
    setSavingEmail(true);
    try {
      const { data } = await api.put("/admin/settings/general", { support_email: supportEmail.trim() });
      setSupportEmail(data.support_email);
      toast.success("Contact email updated — live across the site immediately");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingEmail(false); }
  };

  const loadCampaigns = async () => {
    try {
      const { data } = await api.get("/admin/marketing/campaigns");
      setCampaigns(data.campaigns);
    } catch (e) { /* non-fatal */ }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/content/landing");
      setC(data.content);
      setDirty(data.dirty);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const loadTemplates = async (selectId) => {
    try {
      const { data } = await api.get("/admin/marketing/templates");
      setTemplates(data.templates);
      const pick = data.templates.find((t) => t.id === selectId) || data.templates[0];
      if (pick) { setActiveTid(pick.id); setEm(pick); }
    } catch (e) { /* non-fatal */ }
  };
  useEffect(() => { load(); loadTemplates(); loadCampaigns(); loadGeneral(); }, []);

  const setEmField = (k, v) => setEm((p) => ({ ...p, [k]: v }));
  const contentPayload = () => ({
    subject: em.subject, body: em.body, company_name: em.company_name,
    website: em.website, app_store: em.app_store, play_store: em.play_store,
  });
  const emPayload = () => ({ name: em.name || "Untitled", ...contentPayload() });

  const selectTemplate = (id) => {
    if (!id) { newTemplate(); return; }
    const t = templates.find((x) => x.id === id);
    if (t) { setActiveTid(id); setEm(t); setEmPreview(null); }
  };

  const newTemplate = () => {
    setActiveTid("");
    setEm({ name: "Untitled template", subject: "", body: "", company_name: em?.company_name || "", website: em?.website || "", app_store: em?.app_store || "", play_store: em?.play_store || "" });
    setEmPreview(null);
  };

  const saveTemplate = async () => {
    if (!em.name?.trim()) { toast.error("Give the template a name first."); return; }
    setEmSaving(true);
    try {
      if (activeTid) {
        await api.put(`/admin/marketing/templates/${activeTid}`, emPayload());
        toast.success("Template updated");
        await loadTemplates(activeTid);
      } else {
        const { data } = await api.post("/admin/marketing/templates", emPayload());
        toast.success("Template saved");
        await loadTemplates(data.id);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setEmSaving(false); }
  };

  const saveAsNew = async () => {
    const name = window.prompt("Name for the new template:", `${em.name || "Untitled"} copy`);
    if (!name) return;
    try {
      const { data } = await api.post("/admin/marketing/templates", { ...emPayload(), name });
      toast.success("Saved as new template");
      await loadTemplates(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const deleteTemplate = async () => {
    if (!activeTid) { newTemplate(); return; }
    if (!window.confirm(`Delete template "${em.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/marketing/templates/${activeTid}`);
      toast.success("Template deleted");
      await loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const previewEmail = async () => {
    try {
      const { data } = await api.post("/admin/marketing/email/preview", contentPayload());
      setEmPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const sendEmail = async () => {
    const list = recipients.split(/[,\s;]+/).filter(Boolean);
    if (list.length === 0) { toast.error("Enter at least one recipient email."); return; }
    if (!window.confirm(`Send this email to ${list.length} recipient(s)?`)) return;
    setEmSending(true);
    try {
      const { data } = await api.post("/admin/marketing/email/send", { ...contentPayload(), recipients: list });
      toast.success(`Sent to ${data.sent} of ${data.requested} recipient(s)`);
      if (data.invalid?.length) toast.warning(`Skipped ${data.invalid.length} invalid address(es)`);
      if (data.suppressed?.length) toast.info(`${data.suppressed.length} unsubscribed address(es) skipped`);
      if (data.failed?.length) toast.warning(`${data.failed.length} failed to send`);
      loadCampaigns();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setEmSending(false); }
  };

  const fillAudience = async (group) => {
    try {
      const { data } = await api.get("/admin/marketing/recipients", { params: { group } });
      if (data.count === 0) { toast.info("No consented recipients found in that group."); return; }
      setRecipients((prev) => {
        const existing = new Set(prev.split(/[,\s;]+/).filter(Boolean).map((x) => x.toLowerCase()));
        const merged = [...existing];
        data.emails.forEach((e) => { if (!existing.has(e)) merged.push(e); });
        return merged.join(", ");
      });
      toast.success(`Added ${data.count} ${group === "all" ? "user" : group.slice(0, -1)} email(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const payload = () => ({
    hero: c.hero,
    how_eyebrow: c.how_eyebrow,
    how_title: c.how_title,
    steps: c.steps,
    testimonials_eyebrow: c.testimonials_eyebrow,
    testimonials_title: c.testimonials_title,
    testimonials: c.testimonials,
  });

  const setHero = (k, v) => setC((p) => ({ ...p, hero: { ...p.hero, [k]: v } }));
  const setStep = (i, k, v) => setC((p) => { const s = [...p.steps]; s[i] = { ...s[i], [k]: v }; return { ...p, steps: s }; });
  const setTesti = (i, k, v) => setC((p) => { const t = [...p.testimonials]; t[i] = { ...t[i], [k]: v }; return { ...p, testimonials: t }; });
  const addTesti = () => setC((p) => ({ ...p, testimonials: [...(p.testimonials || []), { name: "", quote: "", rating: 5 }] }));
  const removeTesti = (i) => setC((p) => ({ ...p, testimonials: p.testimonials.filter((_, x) => x !== i) }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/content/landing/upload-image", fd);
      setHero("image_url", data.path);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content/landing", payload());
      setC(data.content);
      setDirty(true);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/content/landing/publish", payload());
      setC(data.content);
      setDirty(false);
      toast.success("Published — your changes are now live");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPublishing(false);
    }
  };

  const preview = () => window.open("/?preview=1", "_blank");

  if (!c) return <div className="container-x py-12" data-testid="admin-marketing"><AdminTabs /><p className="text-[#5C6B64]">Loading…</p></div>;

  return (
    <div className="container-x py-12" data-testid="admin-marketing">
      <AdminTabs />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2">Marketing</div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
            <LayoutTemplate className="w-6 h-6 text-[#2D6A4F]" /> Landing page editor
          </h1>
          <p className="text-[#5C6B64] mt-2 max-w-2xl">Edit the public homepage content — hero, value props, testimonials, and the hero image. Save a draft, preview it, then <strong>Publish</strong> when you're ready to go live.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {dirty && <span data-testid="marketing-dirty-badge" className="text-xs font-medium text-[#CC5A3A] inline-flex items-center gap-1.5 mr-1"><span className="w-2 h-2 rounded-full bg-[#CC5A3A]" /> Unpublished changes</span>}
          <button data-testid="marketing-preview" onClick={preview} className="btn-secondary text-sm py-2 px-3 inline-flex items-center gap-2"><ExternalLink className="w-4 h-4" /> Preview draft</button>
          <button data-testid="marketing-save" onClick={saveDraft} disabled={saving} className="btn-secondary text-sm py-2 px-4 inline-flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save draft"}
          </button>
          <button data-testid="marketing-publish" onClick={publish} disabled={publishing} className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-2 disabled:opacity-50">
            <UploadCloud className="w-4 h-4" /> {publishing ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <Card title="Hero section">
          <Field label="Eyebrow" testid="hero-eyebrow-input" value={c.hero.eyebrow} onChange={(v) => setHero("eyebrow", v)} />
          <Field label="Heading" testid="hero-heading-input" value={c.hero.heading} onChange={(v) => setHero("heading", v)} textarea />
          <Field label="Subtext" testid="hero-subtext-input" value={c.hero.subtext} onChange={(v) => setHero("subtext", v)} textarea />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Primary button text" testid="hero-cta1-label" value={c.hero.primary_cta_label} onChange={(v) => setHero("primary_cta_label", v)} />
            <Field label="Primary button link" testid="hero-cta1-link" value={c.hero.primary_cta_link} onChange={(v) => setHero("primary_cta_link", v)} placeholder="/search" />
            <Field label="Secondary button text" testid="hero-cta2-label" value={c.hero.secondary_cta_label} onChange={(v) => setHero("secondary_cta_label", v)} />
            <Field label="Secondary button link" testid="hero-cta2-link" value={c.hero.secondary_cta_link} onChange={(v) => setHero("secondary_cta_link", v)} placeholder="/register" />
          </div>
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Hero image</span>
            <div className="mt-2 flex items-center gap-4">
              <img src={resolveLandingImg(c.hero.image_url, "https://images.unsplash.com/photo-1628744448839-a475cc0e90c3?w=200&q=60")} alt="hero preview" className="w-28 h-20 rounded-lg object-cover border border-[#1E3F33]/10" />
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} data-testid="hero-image-upload" className="text-xs" />
                {uploading && <span className="text-xs text-[#8A9A86]">Uploading…</span>}
                {c.hero.image_url && (
                  <button type="button" onClick={() => setHero("image_url", "")} className="text-xs text-[#CC5A3A] text-left inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Reset to default image</button>
                )}
              </div>
            </div>
            <Field label="…or paste an image URL" testid="hero-image-url" value={c.hero.image_url} onChange={(v) => setHero("image_url", v)} placeholder="https://…" />
          </div>
        </Card>

        <Card title="How it works">
          <Field label="Eyebrow" testid="how-eyebrow-input" value={c.how_eyebrow} onChange={(v) => setC((p) => ({ ...p, how_eyebrow: v }))} />
          <Field label="Section title" testid="how-title-input" value={c.how_title} onChange={(v) => setC((p) => ({ ...p, how_title: v }))} />
          <div className="space-y-4">
            {(c.steps || []).map((s, i) => (
              <div key={i} className="rounded-xl border border-[#1E3F33]/10 p-4 space-y-3" data-testid={`step-editor-${i}`}>
                <div className="flex items-center gap-3">
                  <select data-testid={`step-icon-${i}`} value={s.icon} onChange={(e) => setStep(i, "icon", e.target.value)} className="rounded-lg border border-[#1E3F33]/15 bg-white px-2.5 py-2 text-sm">
                    {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <input data-testid={`step-title-${i}`} className="input-soft flex-1" value={s.title || ""} placeholder="Step title" onChange={(e) => setStep(i, "title", e.target.value)} />
                </div>
                <textarea data-testid={`step-text-${i}`} className="input-soft w-full min-h-[70px]" value={s.text || ""} placeholder="Step description" onChange={(e) => setStep(i, "text", e.target.value)} />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Testimonials">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Eyebrow" testid="testi-eyebrow-input" value={c.testimonials_eyebrow} onChange={(v) => setC((p) => ({ ...p, testimonials_eyebrow: v }))} />
            <Field label="Section title" testid="testi-title-input" value={c.testimonials_title} onChange={(v) => setC((p) => ({ ...p, testimonials_title: v }))} />
          </div>
          <div className="space-y-4">
            {(c.testimonials || []).map((t, i) => (
              <div key={i} className="rounded-xl border border-[#1E3F33]/10 p-4 space-y-3" data-testid={`testi-editor-${i}`}>
                <div className="flex items-center gap-3">
                  <input data-testid={`testi-name-${i}`} className="input-soft flex-1" value={t.name || ""} placeholder="Customer name" onChange={(e) => setTesti(i, "name", e.target.value)} />
                  <select data-testid={`testi-rating-${i}`} value={t.rating || 5} onChange={(e) => setTesti(i, "rating", parseInt(e.target.value))} className="rounded-lg border border-[#1E3F33]/15 bg-white px-2.5 py-2 text-sm">
                    {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} ★</option>)}
                  </select>
                  <button type="button" data-testid={`testi-remove-${i}`} onClick={() => removeTesti(i)} className="text-[#CC5A3A] p-2 hover:bg-[#CC5A3A]/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
                <textarea data-testid={`testi-quote-${i}`} className="input-soft w-full min-h-[70px]" value={t.quote || ""} placeholder="Quote" onChange={(e) => setTesti(i, "quote", e.target.value)} />
              </div>
            ))}
            <button type="button" data-testid="testi-add" onClick={addTesti} className="btn-secondary text-sm py-2 px-3 inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add testimonial</button>
            <p className="text-xs text-[#8A9A86]">The testimonials section is hidden on the homepage until at least one is added.</p>
          </div>
        </Card>
      </div>

      <section className="mt-8 rounded-2xl border border-[#1E3F33]/10 bg-white p-6 space-y-4" data-testid="general-settings">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#2D6A4F]" />
          <h2 className="font-heading text-lg text-[#1E3F33]">Contact &amp; support email</h2>
        </div>
        <p className="text-sm text-[#5C6B64]">The email shown in the site footer, legal pages, billing notes and welcome emails. Changes apply everywhere <strong>instantly</strong> — no redeploy needed.</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[240px]">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Support email</span>
            <input data-testid="support-email-input" type="email" className="input-soft mt-1.5 w-full" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} placeholder="support@yourdomain.com" />
          </label>
          <button data-testid="support-email-save" onClick={saveSupportEmail} disabled={savingEmail || !supportEmail.trim()} className="btn-primary text-sm py-2.5 px-4 inline-flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" /> {savingEmail ? "Saving…" : "Save email"}
          </button>
        </div>
      </section>

      {em && (
        <section className="mt-8 rounded-2xl border border-[#1E3F33]/10 bg-white p-6 space-y-4" data-testid="campaign-email">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#2D6A4F]" />
            <h2 className="font-heading text-lg text-[#1E3F33]">Campaign / invite email</h2>
          </div>

          <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-[#F7F6F2] border border-[#1E3F33]/8">
            <label className="flex-1 min-w-[200px]">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Template</span>
              <select data-testid="template-select" className="input-soft mt-1.5 w-full" value={activeTid} onChange={(e) => selectTemplate(e.target.value)}>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                {!activeTid && <option value="">Untitled (unsaved)</option>}
              </select>
            </label>
            <div className="flex flex-wrap gap-2">
              <button type="button" data-testid="template-new" onClick={newTemplate} className="btn-secondary text-xs py-2 px-3 inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New</button>
              <button type="button" data-testid="template-save" onClick={saveTemplate} disabled={emSaving} className="btn-secondary text-xs py-2 px-3 inline-flex items-center gap-1.5 disabled:opacity-50"><Save className="w-3.5 h-3.5" /> {activeTid ? "Save" : "Save template"}</button>
              <button type="button" data-testid="template-saveas" onClick={saveAsNew} className="btn-secondary text-xs py-2 px-3">Save as new</button>
              <button type="button" data-testid="template-delete" onClick={deleteTemplate} className="text-xs py-2 px-3 text-[#CC5A3A] hover:underline inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          </div>

          <Field label="Template name" testid="email-name" value={em.name} onChange={(v) => setEmField("name", v)} placeholder="e.g. Marketplace invite" />

          <p className="text-sm text-[#5C6B64]">
            Send a marketplace invite to one or more people. Use the placeholders{" "}
            <code className="text-xs bg-[#F0EFEA] px-1 py-0.5 rounded">{"{{company_name}}"}</code>,{" "}
            <code className="text-xs bg-[#F0EFEA] px-1 py-0.5 rounded">{"{{website}}"}</code>,{" "}
            <code className="text-xs bg-[#F0EFEA] px-1 py-0.5 rounded">{"{{app_store}}"}</code>,{" "}
            <code className="text-xs bg-[#F0EFEA] px-1 py-0.5 rounded">{"{{play_store}}"}</code> — they're filled in automatically. Edit the body freely before sending.
          </p>

          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Quick add audience (consented only)</span>
            <div className="mt-1.5 flex flex-wrap gap-2">
              <button type="button" data-testid="audience-collectors" onClick={() => fillAudience("collectors")} className="btn-secondary text-xs py-1.5 px-3">All collectors</button>
              <button type="button" data-testid="audience-customers" onClick={() => fillAudience("customers")} className="btn-secondary text-xs py-1.5 px-3">All customers</button>
              <button type="button" data-testid="audience-all" onClick={() => fillAudience("all")} className="btn-secondary text-xs py-1.5 px-3">All users</button>
              {recipients && <button type="button" data-testid="audience-clear" onClick={() => setRecipients("")} className="text-xs py-1.5 px-3 text-[#CC5A3A] hover:underline">Clear</button>}
            </div>
          </div>

          <Field label="Recipients (comma, space, or newline separated)" testid="email-recipients" value={recipients} onChange={setRecipients} textarea placeholder="jane@example.com, collector@example.com" />

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Company / marketplace name" testid="email-company" value={em.company_name} onChange={(v) => setEmField("company_name", v)} />
            <Field label="Website link" testid="email-website" value={em.website} onChange={(v) => setEmField("website", v)} placeholder="https://…" />
            <Field label="Apple App Store link" testid="email-appstore" value={em.app_store} onChange={(v) => setEmField("app_store", v)} placeholder="https://apps.apple.com/…" />
            <Field label="Google Play link" testid="email-playstore" value={em.play_store} onChange={(v) => setEmField("play_store", v)} placeholder="https://play.google.com/…" />
          </div>

          <Field label="Subject" testid="email-subject" value={em.subject} onChange={(v) => setEmField("subject", v)} />
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Email body (editable)</span>
            <textarea data-testid="email-body" className="input-soft mt-1.5 w-full min-h-[280px] font-mono text-sm" value={em.body || ""} onChange={(e) => setEmField("body", e.target.value)} />
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="email-preview-btn" onClick={previewEmail} className="btn-secondary text-sm py-2 px-3 inline-flex items-center gap-2"><Eye className="w-4 h-4" /> Preview</button>
            <button data-testid="email-send-btn" onClick={sendEmail} disabled={emSending} className="btn-primary text-sm py-2 px-4 inline-flex items-center gap-2 disabled:opacity-50"><Send className="w-4 h-4" /> {emSending ? "Sending…" : "Send email"}</button>
          </div>

          {emPreview && (
            <div className="mt-2 rounded-xl border border-[#1E3F33]/10 overflow-hidden" data-testid="email-preview-box">
              <div className="bg-[#F0EFEA] px-4 py-2 text-sm text-[#1E3F33]"><strong>Subject:</strong> {emPreview.subject}</div>
              <div className="p-5 bg-white" dangerouslySetInnerHTML={{ __html: emPreview.html }} />
            </div>
          )}

          {campaigns.length > 0 && (
            <div className="mt-4" data-testid="campaign-history">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64] mb-2">Campaign history</h3>
              <div className="rounded-xl border border-[#1E3F33]/10 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
                    <th className="px-4 py-2.5 font-medium">Sent</th>
                    <th className="px-4 py-2.5 font-medium">Subject</th>
                    <th className="px-4 py-2.5 font-medium">Delivered</th>
                    <th className="px-4 py-2.5 font-medium">Opens</th>
                    <th className="px-4 py-2.5 font-medium">Clicks</th>
                    <th className="px-4 py-2.5 font-medium">Failed</th>
                    <th className="px-4 py-2.5 font-medium">Unsub-skipped</th>
                  </tr></thead>
                  <tbody>
                    {campaigns.map((c) => (
                      <tr key={c.id} className="border-t border-[#1E3F33]/8" data-testid={`campaign-row-${c.id}`}>
                        <td className="px-4 py-2.5 text-[#5C6B64] whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleString() : "—"}</td>
                        <td className="px-4 py-2.5 text-[#1E3F33] max-w-[240px] truncate">{c.subject}</td>
                        <td className="px-4 py-2.5 text-[#2D6A4F]">{c.sent} / {c.recipient_count}</td>
                        <td className="px-4 py-2.5 text-[#1E3F33]">{c.opens} <span className="text-[#8A9A86]">({c.open_rate}%)</span></td>
                        <td className="px-4 py-2.5 text-[#1E3F33]">{c.clicks} <span className="text-[#8A9A86]">({c.click_rate}%)</span></td>
                        <td className={`px-4 py-2.5 ${c.failed_count ? "text-[#E63946]" : "text-[#8A9A86]"}`}>{c.failed_count}</td>
                        <td className="px-4 py-2.5 text-[#8A9A86]">{c.suppressed_count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      <div className="mt-8 flex justify-end gap-2">
        <button data-testid="marketing-save-bottom" onClick={saveDraft} disabled={saving} className="btn-secondary text-sm py-2.5 px-5 inline-flex items-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save draft"}
        </button>
        <button data-testid="marketing-publish-bottom" onClick={publish} disabled={publishing} className="btn-primary text-sm py-2.5 px-5 inline-flex items-center gap-2 disabled:opacity-50">
          <UploadCloud className="w-4 h-4" /> {publishing ? "Publishing…" : "Publish"}
        </button>
      </div>
    </div>
  );
}
